import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  VStack,
  HStack,
  Text,
  useToast,
  useColorModeValue,
} from "@chakra-ui/react";
import { ArrowBackIcon } from "@chakra-ui/icons";
import { timesheetService, projectService } from "../services/edosService";

export default function TimesheetCreatePage() {
  const navigate = useNavigate();
  const toast = useToast();
  const [searchParams] = useSearchParams();

  const defaultProjectId = searchParams.get("projectId") || "";

  const bg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");

  const [projects, setProjects] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    date: new Date().toISOString().split("T")[0],
    totalMinutes: "",
    billable: true,
    projectId: defaultProjectId,
    ticketId: "",
    sprintId: "",
    notes: "",
  });

  const fetchProjects = useCallback(async () => {
    try {
      const res = await projectService.list();
      setProjects(res.data.data || res.data.projects || []);
    } catch {
      setProjects([]);
    }
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const handleCreate = async () => {
    if (!form.date || !form.totalMinutes) {
      toast({ title: "Date and duration are required", status: "warning" });
      return;
    }

    setSubmitting(true);
    try {
      const payload = { ...form, totalMinutes: Number(form.totalMinutes) };
      if (!payload.projectId) delete payload.projectId;
      if (!payload.ticketId) delete payload.ticketId;
      if (!payload.sprintId) delete payload.sprintId;

      await timesheetService.log(payload);
      toast({ title: "Time logged", status: "success" });
      navigate(
        `/edos/timesheets${form.projectId ? `?projectId=${form.projectId}` : ""}`,
      );
    } catch (err) {
      toast({
        title: err?.response?.data?.message || "Failed to log time",
        status: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box p={6}>
      <HStack mb={5} spacing={3}>
        <Button
          leftIcon={<ArrowBackIcon />}
          variant="ghost"
          onClick={() =>
            navigate(
              `/edos/timesheets${form.projectId ? `?projectId=${form.projectId}` : ""}`,
            )
          }
        >
          Back
        </Button>
        <Text fontSize="2xl" fontWeight="bold">
          Log Time
        </Text>
      </HStack>

      <Box
        bg={bg}
        border="1px"
        borderColor={borderColor}
        borderRadius="lg"
        p={6}
        maxW="760px"
      >
        <VStack spacing={4} align="stretch">
          <HStack align="start" spacing={4}>
            <FormControl isRequired>
              <FormLabel>Date</FormLabel>
              <Input
                type="date"
                value={form.date}
                onChange={(e) =>
                  setForm((f) => ({ ...f, date: e.target.value }))
                }
              />
            </FormControl>
            <FormControl isRequired>
              <FormLabel>Duration (minutes)</FormLabel>
              <Input
                type="number"
                value={form.totalMinutes}
                onChange={(e) =>
                  setForm((f) => ({ ...f, totalMinutes: e.target.value }))
                }
                placeholder="e.g. 90"
              />
            </FormControl>
          </HStack>

          <FormControl>
            <FormLabel>Project</FormLabel>
            <Select
              value={form.projectId}
              onChange={(e) =>
                setForm((f) => ({ ...f, projectId: e.target.value }))
              }
            >
              <option value="">None</option>
              {projects.map((p) => (
                <option key={p._id} value={p._id}>
                  {p.projectName}
                </option>
              ))}
            </Select>
          </FormControl>

          <FormControl>
            <FormLabel>Ticket ID (optional)</FormLabel>
            <Input
              value={form.ticketId}
              onChange={(e) =>
                setForm((f) => ({ ...f, ticketId: e.target.value }))
              }
              placeholder="Ticket ObjectId"
            />
          </FormControl>

          <FormControl>
            <FormLabel>Sprint ID (optional)</FormLabel>
            <Input
              value={form.sprintId}
              onChange={(e) =>
                setForm((f) => ({ ...f, sprintId: e.target.value }))
              }
              placeholder="Sprint ObjectId"
            />
          </FormControl>

          <FormControl>
            <FormLabel>Notes</FormLabel>
            <Input
              value={form.notes}
              onChange={(e) =>
                setForm((f) => ({ ...f, notes: e.target.value }))
              }
              placeholder="What did you work on?"
            />
          </FormControl>

          <FormControl>
            <FormLabel>Billable</FormLabel>
            <Select
              value={String(form.billable)}
              onChange={(e) =>
                setForm((f) => ({ ...f, billable: e.target.value === "true" }))
              }
            >
              <option value="true">Yes</option>
              <option value="false">No</option>
            </Select>
          </FormControl>

          <HStack justify="flex-end" pt={2}>
            <Button
              variant="ghost"
              onClick={() =>
                navigate(
                  `/edos/timesheets${form.projectId ? `?projectId=${form.projectId}` : ""}`,
                )
              }
            >
              Cancel
            </Button>
            <Button
              colorScheme="orange"
              isLoading={submitting}
              onClick={handleCreate}
            >
              Log Time
            </Button>
          </HStack>
        </VStack>
      </Box>
    </Box>
  );
}
